import { ArrowRight, Building2, ClipboardCheck, Package2, PanelsTopLeft, PhoneCall, Truck } from "lucide-react";
import { PHOTOS } from "@/lib/site";

const advantages = [
  { icon: Building2, label: "Jaringan Mitra" },
  { icon: PhoneCall, label: "Dukungan Tim Sales" },
  { icon: Truck, label: "Pengiriman Nasional" },
  { icon: ClipboardCheck, label: "Respons Kebutuhan Proyek" },
  { icon: PanelsTopLeft, label: "Portofolio Aplikasi" },
  { icon: Package2, label: "Pasokan Multi Produk" },
];

export const Hero = () => {
  return (
    <section
      id="beranda"
      data-testid="hero-section"
      className="relative min-h-[88vh] sm:min-h-screen pt-24 sm:pt-32 pb-14 sm:pb-24 overflow-hidden"
    >
      <div className="absolute inset-0 hero-radial" />
      <div className="absolute inset-0 grid-pattern opacity-40" />
      <div className="absolute inset-0">
        <img
          src={PHOTOS.kantorDisplay}
          alt="Kantor Display PT Animax Putra Lancar"
          className="w-full h-full object-cover object-center opacity-[0.72]"
          style={{
            WebkitMaskImage: "linear-gradient(to right, transparent 0%, rgba(0,0,0,0.18) 34%, rgba(0,0,0,0.72) 62%, black 100%)",
            maskImage: "linear-gradient(to right, transparent 0%, rgba(0,0,0,0.18) 34%, rgba(0,0,0,0.72) 62%, black 100%)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/70 to-background/10" />
        <div className="absolute inset-0 bg-gradient-to-b from-background/75 via-transparent to-background" />
        <div className="absolute inset-0 bg-animax-yellow/10" />
      </div>

      <div className="max-w-7xl mx-auto px-5 sm:px-6 relative">
        <div className="grid lg:grid-cols-12 gap-8 sm:gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex max-w-full items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full glass-light mb-4 sm:mb-6" data-testid="hero-badge">
              <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-animax-orange animate-pulse" />
              <span className="tag-uppercase text-[10px] sm:text-xs text-neutral-700">PT. Animax Putra Lancar</span>
            </div>

            <h1 className="font-display text-3xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[1.04] tracking-tight sm:tracking-tighter font-medium text-animax-black">
              Solusi Material untuk{" "}
              <span className="relative inline-block">
                <span className="relative z-10">Proyek & Industri</span>
                <span className="absolute bottom-0.5 sm:bottom-1 left-0 right-0 h-2 sm:h-3 bg-animax-yellow -z-0 opacity-80" />
              </span>
            </h1>

            <p className="mt-4 sm:mt-6 text-sm sm:text-lg text-neutral-600 leading-relaxed max-w-2xl">
              Menyediakan pilihan material dan produk pendukung untuk kebutuhan proyek, kontraktor,
              retail, display, interior, hingga industri dengan layanan responsif dan dukungan tim
              sales yang mudah dihubungi.
            </p>

            <div className="mt-7 sm:mt-10 flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-4">
              <a
                href="/#produk"
                data-testid="hero-cta-product"
                className="group inline-flex items-center justify-center gap-2 px-5 sm:px-7 py-3 sm:py-4 rounded-full bg-animax-orange text-white font-medium text-sm hover:bg-animax-yellow hover:text-animax-black transition-all shadow-lg shadow-animax-orange/25"
              >
                <Package2 className="w-4 h-4" />
                Lihat Produk Kami
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href="/#kontak"
                data-testid="hero-cta-quote"
                className="inline-flex items-center justify-center gap-2 px-5 sm:px-7 py-3 sm:py-4 rounded-full border border-animax-black text-animax-black font-medium text-sm hover:bg-animax-black hover:text-white transition-all"
              >
                Hubungi PT APL
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>

            <div className="mt-8 sm:mt-12 grid grid-cols-2 gap-3 sm:flex sm:items-center sm:gap-8">
              <div className="min-w-0">
                <div className="font-display text-xl sm:text-3xl font-medium text-animax-black">2021</div>
                <div className="tag-uppercase text-[10px] sm:text-xs text-neutral-500 mt-1 leading-snug">Berdiri Sejak</div>
              </div>
              <div className="hidden sm:block w-px h-10 bg-neutral-300" />
              <div className="min-w-0 border-l border-neutral-300 pl-3 sm:border-l-0 sm:pl-0">
                <div className="font-display text-lg sm:text-3xl font-medium text-animax-black leading-tight">Mojokerto</div>
                <div className="tag-uppercase text-[10px] sm:text-xs text-neutral-500 mt-1 leading-snug">Jawa Timur</div>
              </div>
            </div>
          </div>

          <div className="hidden lg:block lg:col-span-5" data-testid="hero-visual" />
        </div>

        <div className="mt-9 sm:mt-24 -mx-5 px-5 sm:mx-0 sm:px-0 flex sm:grid sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4 overflow-x-auto sm:overflow-visible snap-x snap-mandatory pb-2 sm:pb-0">
          {advantages.map((a, i) => (
            <div
              key={i}
              data-testid={`hero-advantage-${i}`}
              className="card-lift group shrink-0 w-36 sm:w-auto snap-start p-3 sm:p-5 rounded-xl sm:rounded-2xl bg-white border border-neutral-200/70"
            >
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-neutral-100 flex items-center justify-center mb-2 sm:mb-3 group-hover:bg-animax-yellow transition-colors">
                <a.icon className="w-4 h-4 sm:w-5 sm:h-5 text-animax-black" />
              </div>
              <div className="text-xs sm:text-sm font-medium text-animax-black leading-snug">{a.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
